import { useCallback, useEffect, useState } from 'react';
import axiosPrivate from 'config/axiosPrivate';

export const getProjectId = (project) => {
  const id = project?.id ?? project?.projectId;
  if (id == null || id === '') return null;
  return String(id);
};

export const isSameProject = (projectA, projectB) => {
  if (!projectA || !projectB) return false;
  const idA = getProjectId(projectA);
  const idB = getProjectId(projectB);
  if (idA && idB) return idA === idB;
  return projectA === projectB;
};

export const getProjectDisplayName = (project) => {
  if (!project) return '';
  return (
    project.projectTitle ||
    ''
  ).toString().trim();
};

export const getProjectLabel = (project) => {
  const id = getProjectId(project) ?? '';
  const name = getProjectDisplayName(project);
  if (id && name) return `${id} : ${name}`;
  if (name) return name;
  if (id) return String(id);
  return '';
};

export const useAssignTextToProject = ({ websiteId, onAssigned }) => {
  const [isPopinOpen, setIsPopinOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [textPendingAssign, setTextPendingAssign] = useState(null);
  const [assignedProject, setAssignedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [assignError, setAssignError] = useState('');

  const openAssignPopin = useCallback((row) => {
    setTextPendingAssign(row || null);
    setAssignError('');
    setIsPopinOpen(true);
  }, []);

  const closeAssignPopin = useCallback(() => {
    setIsPopinOpen(false);
    setTextPendingAssign(null);
    setAssignError('');
  }, []);

  const closeSuccessModal = useCallback(() => {
    setIsSuccessModalOpen(false);
    setAssignedProject(null);
    setTextPendingAssign(null);
  }, []);

  useEffect(() => {
    if (!isPopinOpen || !websiteId) {
      if (!isPopinOpen) {
        setProjects([]);
      }
      return undefined;
    }

    let isMounted = true;

    const fetchProjects = async () => {
      setLoadingProjects(true);
      setAssignError('');
      try {
        const response = await axiosPrivate.get(
          `/project/by-website/${websiteId}/summary`
        );
        if (!isMounted) return;
        const list = Array.isArray(response?.data) ? response.data : [];
        setProjects(list);
      } catch (error) {
        console.error('Erreur lors du chargement des projets:', error);
        if (isMounted) {
          setProjects([]);
          setAssignError('assign_project_load_error');
        }
      } finally {
        if (isMounted) {
          setLoadingProjects(false);
        }
      }
    };

    fetchProjects();

    return () => {
      isMounted = false;
    };
  }, [isPopinOpen, websiteId]);

  const handleAssign = useCallback(async (project) => {
    const textId = textPendingAssign?.id;
    const projectId = getProjectId(project);

    if (!textId || !projectId) {
      setAssignError('assign_project_selection_error');
      return false;
    }

    setIsSaving(true);
    setAssignError('');

    try {
      await axiosPrivate.put(
        `/texts/external/unassigned/${textId}/project/${projectId}`
      );
      setAssignedProject(project);
      setIsPopinOpen(false);
      setIsSuccessModalOpen(true);
      if (typeof onAssigned === 'function') {
        onAssigned(textId, project);
      }
      return true;
    } catch (error) {
      console.error('Erreur lors de l\'assignation au projet:', error);
      setAssignError('assign_project_save_error');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [onAssigned, textPendingAssign?.id]);

  return {
    isPopinOpen,
    isSuccessModalOpen,
    textPendingAssign,
    assignedProject,
    projects,
    loadingProjects,
    isSaving,
    assignError,
    openAssignPopin,
    closeAssignPopin,
    closeSuccessModal,
    handleAssign,
  };
};
